import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import { canAccessPath, firstAllowedPath } from './lib/pageAccess';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { useAuth } from './hooks/useAuth';
import { useImpersonation } from './context/ImpersonationContext';
// ─── Eager imports: public / donor-facing routes, auth entry, and the layout
// shells. These stay in the main bundle so a donor (or any guest) NEVER waits
// on a lazily-loaded chunk — and so a deploy can never produce a chunk-load
// error on the public donation flow. Everything authenticated/admin is lazy
// (below) and split into its own chunk, which is what shrinks the bundle a
// public visitor downloads.
import LoginPage from './pages/LoginPage';
import ChangePasswordRequiredPage from './pages/ChangePasswordRequiredPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AccessibilityStatementPage from './pages/AccessibilityStatementPage';
import AccessibilityWidget from './components/AccessibilityWidget';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailedPage from './pages/PaymentFailedPage';
import LandingPageV2 from './pages/LandingPageV2';
import NotFoundPage from './pages/NotFoundPage';
import MarketingOptOutPage from './pages/MarketingOptOutPage';
import ShortLinkRedirectPage from './pages/ShortLinkRedirectPage';
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './pages/admin/AdminLayout';
import PublicCampaign from './pages/PublicCampaign';
import PaymentLinkPage from './pages/PaymentLinkPage';
import ThankYouPage from './pages/ThankYouPage';
import PublicAmbassadorDashboard from './pages/PublicAmbassadorDashboard';
import PublicCampaignManagerDashboard from './pages/PublicCampaignManagerDashboard';
import KnowledgePage from './pages/KnowledgePage';
import ArticlePage from './pages/ArticlePage';
import RegisterPage from './pages/RegisterPage';
import QuickRegisterPage from './pages/QuickRegisterPage';
import PublicTool from './pages/PublicTool';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AgentsTasksPage from './pages/AgentsTasksPage';
import PublicVolunteerPage from './pages/PublicVolunteerPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import VolunteerPortalPage from './pages/VolunteerPortalPage';
import PublicFormPage from './pages/PublicFormPage';
import LandingPage from './pages/LandingPage';
import ContactUsPage from './pages/ContactUsPage';
import ExternalRegisterPage from './pages/ExternalRegisterPage';
import PublicQuote from './pages/PublicQuote';
import PublicPaymentForm from './pages/PublicPaymentForm';
import PublicStrategicPlanPage from './pages/PublicStrategicPlanPage';

// ─── Lazy imports: authenticated org pages, admin portal, and full-screen
// editors. None are on the public/donor path, so code-splitting them out keeps
// them entirely out of a public visitor's download. React.lazy needs a
// <Suspense> ancestor (added around <Routes> below).
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const TasksPage = lazy(() => import('./pages/TasksPage'));
const ContactDetailPage = lazy(() => import('./pages/ContactDetailPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const CampaignBuilderPage = lazy(() => import('./pages/CampaignBuilderPage'));
const DonationsPage = lazy(() => import('./pages/DonationsPage'));
const MembershipsPage = lazy(() => import('./pages/MembershipsPage'));
const VolunteersPage = lazy(() => import('./pages/VolunteersPage'));
const VolunteerProjectsPage = lazy(() => import('./pages/VolunteerProjectsPage'));
const ShiftsPage = lazy(() => import('./pages/ShiftsPage'));
const PermissionsPage = lazy(() => import('./pages/PermissionsPage'));
const VolunteerFormEditor = lazy(() => import('./pages/VolunteerFormEditor'));
const GrantsPage = lazy(() => import('./pages/GrantsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ContactFieldsSettingsPage = lazy(() => import('./pages/ContactFieldsSettingsPage'));
const IntegrationsSettingsPage = lazy(() => import('./pages/IntegrationsSettingsPage'));
const AccountingSettingsPage = lazy(() => import('./pages/AccountingSettingsPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const BlockEditorPageV3 = lazy(() => import('./pages/BlockEditorPageV3'));
const LandingPageBuilderNew = lazy(() => import('./pages/LandingPageBuilder'));
const EasyLandingEditor = lazy(() => import('./pages/EasyLandingEditor'));
const ImportContactsPage = lazy(() => import('./pages/ImportContactsPage'));
const AdminOrganizationsPage = lazy(() => import('./pages/admin/AdminOrganizationsPage'));
const OrgManagementPage = lazy(() => import('./pages/admin/OrgManagementPage'));
const CrmPage = lazy(() => import('./pages/admin/CrmPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AdminAnalyticsPage'));
const AdminRevenueDashboardPage = lazy(() => import('./pages/admin/AdminRevenueDashboardPage'));
const ExpensesPage = lazy(() => import('./pages/ExpensesPage'));
const IncomePage = lazy(() => import('./pages/IncomePage'));
const PaymentDemandsPage = lazy(() => import('./pages/PaymentDemandsPage'));
const MasavPage = lazy(() => import('./pages/MasavPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const AdminArticlesPage = lazy(() => import('./pages/AdminArticlesPage'));
const AdminInvitesPage = lazy(() => import('./pages/admin/AdminInvitesPage'));
const AdminLeadsPage = lazy(() => import('./pages/admin/AdminLeadsPage'));
const AdminReferrersPage = lazy(() => import('./pages/admin/AdminReferrersPage'));
const AdminCalendarPage = lazy(() => import('./pages/admin/AdminCalendarPage'));
const AdminQuotesPage = lazy(() => import('./pages/admin/QuotesPage'));
const PaymentFormsPage = lazy(() => import('./pages/admin/PaymentFormsPage'));
const AdminDevPage = lazy(() => import('./pages/admin/AdminDevPage'));
const AdminLogsPage = lazy(() => import('./pages/admin/AdminLogsPage'));
const SystemTerminalPage = lazy(() => import('./pages/admin/SystemTerminalPage'));
const StrategicPlanPage = lazy(() => import('./pages/admin/StrategicPlanPage'));
const AdminMaytapiPage = lazy(() => import('./pages/admin/AdminMaytapiPage'));
const DebugTranzilaPage = lazy(() => import('./pages/admin/DebugTranzilaPage'));
const CampaignDashboard = lazy(() => import('./pages/CampaignDashboard'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const ToolSubmissionsPage = lazy(() => import('./pages/ToolSubmissionsPage'));
const SignatureEditorPage = lazy(() => import('./pages/SignatureEditorPage'));
const MailingPage = lazy(() => import('./pages/MailingPage'));
const CampaignLinkAnalytics = lazy(() => import('./pages/CampaignLinkAnalytics'));
const LinksPage = lazy(() => import('./pages/LinksPage'));
const MailBuilderPage = lazy(() => import('./pages/MailBuilder/index'));
const SmsCampaignEditor = lazy(() => import('./pages/SmsCampaignEditor'));
const WhatsAppCampaignEditor = lazy(() => import('./pages/WhatsAppCampaignEditor'));
const WhatsAppSettingsPage = lazy(() => import('./pages/WhatsAppSettingsPage'));
const WhatsAppTemplatesPage = lazy(() => import('./pages/WhatsAppTemplatesPage'));
const SmsSendersPage = lazy(() => import('./pages/SmsSendersPage'));
const AdminSmsSendersPage = lazy(() => import('./pages/admin/AdminSmsSendersPage'));
const AdminStuckSmsPage = lazy(() => import('./pages/admin/AdminStuckSmsPage'));
const TelemarketingPage = lazy(() => import('./pages/TelemarketingPage'));
const CallCenterManagerPage = lazy(() => import('./pages/CallCenterManagerPage'));
const CallListDetailPage = lazy(() => import('./pages/CallListDetailPage'));
const DeliveryPage = lazy(() => import('./pages/DeliveryPage'));
const FinancesPage = lazy(() => import('./pages/FinancesPage'));
const BudgetPage = lazy(() => import('./pages/BudgetPage'));
const WorkPlanPage = lazy(() => import('./pages/WorkPlanPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const ProjectFormsPage = lazy(() => import('./pages/ProjectFormsPage'));
const BeneficiariesPage = lazy(() => import('./pages/BeneficiariesPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const CashFlowReportPage = lazy(() => import('./pages/CashFlowReportPage'));

// Home Route — landing לאורחים, dashboard למחוברים
const HomeRoute: React.FC = () => {
  const { user } = useAuth();
  const { isImpersonating } = useImpersonation();
  if (!user) {
    return <Navigate to="/landing" replace />;
  }
  if ((user as any)?.mustChangePassword) {
    return <Navigate to="/change-password-required" replace />;
  }
  // סופר-אדמין מופנה ל-/admin, אלא אם הוא במצב impersonation — אז
  // הוא רואה את ה-dashboard של העמותה כמו משתמש ארגון רגיל.
  if (user.role === 'super_admin' && !isImpersonating) {
    return <Navigate to="/admin" replace />;
  }
  // Dashboard may be blocked for restricted users (e.g. telephonists) — send
  // them to their first accessible page instead.
  if (!canAccessPath('/', user)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }
  return <MainLayout><DashboardPage /></MainLayout>;
};

// Protected Route Component — עבור משתמשי ארגון רגילים
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if ((user as any)?.mustChangePassword) {
    return <Navigate to="/change-password-required" replace />;
  }
  // super_admin מופנה לפורטל האדמין אלא אם הוא במצב impersonation פעיל
  if (user.role === 'super_admin' && !isImpersonating) {
    return <Navigate to="/admin" replace />;
  }
  // Enforce page-level access: a restricted user reaching a blocked page
  // directly (typed URL / bookmark) is redirected to an allowed page.
  if (!canAccessPath(location.pathname, user)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }
  return <MainLayout>{children}</MainLayout>;
};

// Protected route without layout — for full-screen editors (builders)
const ProtectedRouteNoLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { isImpersonating } = useImpersonation();
  const location = useLocation();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if ((user as any)?.mustChangePassword) return <Navigate to="/change-password-required" replace />;
  if (user.role === 'super_admin' && !isImpersonating) return <Navigate to="/admin" replace />;
  if (!canAccessPath(location.pathname, user)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }
  return <>{children}</>;
};

// Admin Route Component — רק לsuper_admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if ((user as any)?.mustChangePassword) {
    return <Navigate to="/change-password-required" replace />;
  }
  if (user.role !== 'super_admin') {
    return <Navigate to="/" replace />;
  }
  return <AdminLayout>{children}</AdminLayout>;
};

function App() {
  const { user } = useAuth();

  // UX safeguard: browsers increment/decrement <input type="number"> on wheel
  // when the input is focused — which silently corrupts values when the user
  // is just scrolling the page. Blurring on wheel preserves the scroll and
  // prevents accidental edits. Applies to every number input app-wide.
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.tagName === 'INPUT' && (t as HTMLInputElement).type === 'number' && document.activeElement === t) {
        (t as HTMLInputElement).blur();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: true });
    return () => document.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <ConfirmProvider>
    <BrowserRouter>
      <ChunkErrorBoundary>
      <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" /></div>}>
      <Routes>
        {/* Auth */}
        <Route
          path="/login"
          element={
            !user ? <LoginPage /> : (
              (user as any)?.mustChangePassword
                ? <Navigate to="/change-password-required" replace />
                : user.role === 'super_admin'
                  ? <Navigate to="/admin" replace />
                  : <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/change-password-required"
          element={user ? <ChangePasswordRequiredPage /> : <Navigate to="/login" replace />}
        />

        {/* Admin Portal — super_admin only */}
        <Route path="/admin" element={<AdminRoute><CrmPage /></AdminRoute>} />
        <Route path="/admin/orgs" element={<AdminRoute><OrgManagementPage /></AdminRoute>} />
        <Route path="/admin/billing-orgs" element={<AdminRoute><AdminOrganizationsPage /></AdminRoute>} />
        <Route path="/admin/revenue" element={<AdminRoute><AdminRevenueDashboardPage /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="/admin/analytics" element={<AdminRoute><AdminAnalyticsPage /></AdminRoute>} />
        <Route path="/admin/articles" element={<AdminRoute><AdminArticlesPage /></AdminRoute>} />
        <Route path="/admin/invites" element={<AdminRoute><AdminInvitesPage /></AdminRoute>} />
        <Route path="/admin/leads" element={<AdminRoute><AdminLeadsPage /></AdminRoute>} />
        <Route path="/admin/referrers" element={<AdminRoute><AdminReferrersPage /></AdminRoute>} />
        <Route path="/admin/calendar" element={<AdminRoute><AdminCalendarPage /></AdminRoute>} />
        <Route path="/admin/quotes" element={<AdminRoute><AdminQuotesPage /></AdminRoute>} />
        <Route path="/admin/payment-forms" element={<AdminRoute><PaymentFormsPage /></AdminRoute>} />
        <Route path="/admin/system-terminal" element={<AdminRoute><SystemTerminalPage /></AdminRoute>} />
        <Route path="/admin/dev" element={<AdminRoute><AdminDevPage /></AdminRoute>} />
        <Route path="/admin/logs" element={<AdminRoute><AdminLogsPage /></AdminRoute>} />
        <Route path="/admin/strategic-plan" element={<AdminRoute><StrategicPlanPage /></AdminRoute>} />
        <Route path="/admin/maytapi" element={<AdminRoute><AdminMaytapiPage /></AdminRoute>} />
        <Route path="/admin/debug-tranzila" element={<AdminRoute><DebugTranzilaPage /></AdminRoute>} />

        {/* Protected — org users */}
        <Route path="/" element={<HomeRoute />} />
        <Route path="/landing" element={<LandingPageV2 />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
        <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
        <Route path="/contacts/:id" element={<ProtectedRoute><ContactDetailPage /></ProtectedRoute>} />
        <Route path="/import/contacts" element={<ProtectedRoute><ImportContactsPage /></ProtectedRoute>} />
        <Route path="/donations" element={<ProtectedRoute><DonationsPage /></ProtectedRoute>} />
        <Route path="/memberships" element={<ProtectedRoute><MembershipsPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
        <Route path="/campaigns/new-builder" element={<ProtectedRouteNoLayout><CampaignBuilderPage /></ProtectedRouteNoLayout>} />
        <Route path="/campaigns/:id/builder" element={<ProtectedRouteNoLayout><CampaignBuilderPage /></ProtectedRouteNoLayout>} />
        <Route path="/campaigns/:id/dashboard" element={<ProtectedRoute><CampaignDashboard /></ProtectedRoute>} />
        <Route path="/gallery" element={<ProtectedRoute><GalleryPage /></ProtectedRoute>} />
        <Route path="/tools" element={<ProtectedRoute><ToolsPage /></ProtectedRoute>} />
        <Route path="/tools/new" element={<ProtectedRoute><BlockEditorPageV3 /></ProtectedRoute>} />
        <Route path="/tools/:id/edit" element={<ProtectedRouteNoLayout><LandingPageBuilderNew /></ProtectedRouteNoLayout>} />
        <Route path="/tools/:id/edit-signature" element={<ProtectedRouteNoLayout><SignatureEditorPage /></ProtectedRouteNoLayout>} />
        <Route path="/tools/:id/submissions" element={<ProtectedRoute><ToolSubmissionsPage /></ProtectedRoute>} />
        <Route path="/events" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/volunteers" element={<ProtectedRoute><VolunteersPage /></ProtectedRoute>} />
        <Route path="/volunteer-projects" element={<ProtectedRoute><VolunteerProjectsPage /></ProtectedRoute>} />
        <Route path="/shifts" element={<ProtectedRoute><ShiftsPage /></ProtectedRoute>} />
        <Route path="/permissions" element={<ProtectedRoute><PermissionsPage /></ProtectedRoute>} />
        <Route path="/volunteer-form-editor" element={<ProtectedRoute><VolunteerFormEditor /></ProtectedRoute>} />
        <Route path="/grants" element={<ProtectedRoute><GrantsPage /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
        <Route path="/income" element={<ProtectedRoute><IncomePage /></ProtectedRoute>} />
        <Route path="/payment-demands" element={<ProtectedRoute><PaymentDemandsPage /></ProtectedRoute>} />
        <Route path="/masav" element={<ProtectedRoute><MasavPage /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/reports/cash-flow" element={<ProtectedRoute><CashFlowReportPage /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/payments/success" element={<PaymentSuccessPage />} />
        <Route path="/payments/complete" element={<PaymentSuccessPage />} />
        <Route path="/payments/failed" element={<PaymentFailedPage />} />
        <Route path="/block-editor" element={<ProtectedRoute><BlockEditorPageV3 /></ProtectedRoute>} />
        <Route path="/easy-landing" element={<ProtectedRoute><EasyLandingEditor /></ProtectedRoute>} />
        <Route path="/mailing" element={<ProtectedRoute><MailingPage /></ProtectedRoute>} />
        <Route path="/mailing/campaigns/:id/links" element={<ProtectedRoute><CampaignLinkAnalytics /></ProtectedRoute>} />
        <Route path="/links" element={<ProtectedRoute><LinksPage /></ProtectedRoute>} />
        <Route path="/mailing/email/new" element={<ProtectedRouteNoLayout><MailBuilderPage /></ProtectedRouteNoLayout>} />
        <Route path="/mailing/email/:id" element={<ProtectedRouteNoLayout><MailBuilderPage /></ProtectedRouteNoLayout>} />
        <Route path="/mailing/sms/new" element={<ProtectedRoute><SmsCampaignEditor /></ProtectedRoute>} />
        <Route path="/mailing/sms/:id" element={<ProtectedRoute><SmsCampaignEditor /></ProtectedRoute>} />
        <Route path="/mailing/whatsapp/new" element={<ProtectedRoute><WhatsAppCampaignEditor /></ProtectedRoute>} />
        <Route path="/mailing/whatsapp/:id" element={<ProtectedRoute><WhatsAppCampaignEditor /></ProtectedRoute>} />
        <Route path="/whatsapp" element={<ProtectedRoute><WhatsAppSettingsPage /></ProtectedRoute>} />
        <Route path="/whatsapp/templates" element={<ProtectedRoute><WhatsAppTemplatesPage /></ProtectedRoute>} />
        <Route path="/sms-senders" element={<ProtectedRoute><SmsSendersPage /></ProtectedRoute>} />
        <Route path="/admin/sms-senders" element={<AdminRoute><AdminSmsSendersPage /></AdminRoute>} />
        <Route path="/admin/stuck-sms" element={<AdminRoute><AdminStuckSmsPage /></AdminRoute>} />
        <Route path="/telemarketing" element={<ProtectedRoute><TelemarketingPage /></ProtectedRoute>} />
        <Route path="/call-center" element={<ProtectedRoute><CallCenterManagerPage /></ProtectedRoute>} />
        {/* Old agent dashboard — merged into /telemarketing. Keep the route
            as a redirect so existing bookmarks / outbound links still work. */}
        <Route path="/call-center/agent" element={<Navigate to="/telemarketing" replace />} />
        <Route path="/call-center/lists/:id" element={<ProtectedRoute><CallListDetailPage /></ProtectedRoute>} />
        <Route path="/delivery" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
        <Route path="/finances" element={<ProtectedRoute><FinancesPage /></ProtectedRoute>} />
        <Route path="/budget" element={<ProtectedRoute><BudgetPage /></ProtectedRoute>} />
        <Route path="/workplan" element={<ProtectedRoute><WorkPlanPage /></ProtectedRoute>} />
        <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
        <Route path="/project-forms" element={<ProtectedRoute><ProjectFormsPage /></ProtectedRoute>} />
        <Route path="/beneficiaries" element={<ProtectedRoute><BeneficiariesPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/settings/contact-fields" element={<ProtectedRoute><ContactFieldsSettingsPage /></ProtectedRoute>} />
        <Route path="/settings/integrations" element={<ProtectedRoute><IntegrationsSettingsPage /></ProtectedRoute>} />
        <Route path="/settings/accounting" element={<ProtectedRoute><AccountingSettingsPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

        {/* Public — no auth */}
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/accessibility" element={<AccessibilityStatementPage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/register/:token" element={<ExternalRegisterPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/quick-register" element={<QuickRegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/campaign/:slug" element={<PublicCampaign />} />
        <Route path="/pay/:token" element={<PaymentLinkPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/payment-failed" element={<PaymentFailedPage />} />
        <Route path="/campaign/:campaignSlug/ambassador/:ambassadorSlug/dashboard" element={<PublicAmbassadorDashboard />} />
        <Route path="/campaign/:slug/manager" element={<PublicCampaignManagerDashboard />} />
        <Route path="/p/:slug" element={<PublicTool />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/knowledge/:slug" element={<ArticlePage />} />

        {/* Agents Tasks — public */}
        <Route path="/agents/tasks" element={<AgentsTasksPage />} />
        <Route path="/volunteers/public/:orgId" element={<PublicVolunteerPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/form/:slug" element={<PublicFormPage />} />
        <Route path="/quote/:token" element={<PublicQuote />} />
        <Route path="/payment/:token" element={<PublicPaymentForm />} />
        <Route path="/strategic-plan/:token" element={<PublicStrategicPlanPage />} />
        <Route path="/u/:token" element={<MarketingOptOutPage />} />
        <Route path="/c/:slug" element={<ShortLinkRedirectPage />} />
        <Route path="/volunteer-portal" element={<VolunteerPortalPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
      {/* תפריט נגישות — מוצג רק בעמודים הציבוריים (ת"י 5568 / WCAG 2.0 AA).
          הרכיב עצמו מסנן לפי הנתיב (PUBLIC_PATHS) ולא מוצג ב-CRM הפנימי. */}
      <AccessibilityWidget />
    </BrowserRouter>
    </ConfirmProvider>
  );
}

export default App;
